import { DownloadSimpleIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { toZonedTime } from 'date-fns-tz'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { useExportChecklists } from '@/api/checklists/export-checklists'
import {
  GET_CHECKLISTS_BASE_QUERY_KEY,
  GetChecklistsRequest,
  getChecklists,
  getChecklistsQueryKey,
  useGetChecklists,
} from '@/api/checklists/get-checklists'
import { getChecklistsResidents } from '@/api/checklists/get-checklists-residents'
import { getChecklistsTemplates } from '@/api/checklists/get-checklists-templates'
import ChecklistsAssignTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsAssignTaskModal/ChecklistsAssignTaskModal'
import ChecklistsCreateNewTaskModal from '@/components/BuildingDashboard/Checklists/ChecklistsCreateNewTaskModal/ChecklistsCreateNewTaskModal'
import LegacyChecklistsDashboard from '@/components/BuildingDashboard/Checklists/Tabs/Dashboard/ChecklistsDashboard'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select, useSelectOptions } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory, routerFactory } from '@/factories'
import { useCaptureEvent, useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import {
  Checklist,
  ChecklistStatus,
  ChecklistType,
  ChecklistTypeEnum,
  LockedResident,
} from '@/models/features'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'

import { ChecklistsPageOutletContext } from '../checklists.page.types'
import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  CHECKLISTS_DASHBOARD_ID,
  checklistsInitialFilters,
  checklistsQueryFiltersSchema,
  getChecklistsColumns,
  makeChecklistTypeOptions,
} from './checklists-dashboard.page.constants'
import {
  ChecklistStatusExtended,
  ChecklistStatusExtendedEnum,
} from './checklists-dashboard.page.types'
import {
  getChecklistStatusColor,
  getChecklistStatusLabel,
  useDefaultFiltering,
} from './checklists-dashboard.page.utils'
import { useChecklistsBulkActions } from './use-checklists-bulk-actions'

export type ChecklistsDashboardPageProps = {
  // Note: when set, scopes the table to a single resident (e.g. embedded in the Resident
  // Profile) - the resident filter is applied directly to the query below and is never stored
  // in `useFilters`/the URL, so it can't be surfaced, edited, or cleared in the UI. Also used
  // to pre-select and lock the resident field on the "Assign Task" CTA.
  resident?: LockedResident
  // Note: overrides the globally-selected building (Redux) with a specific one - needed so an
  // embedded resident's own building is used even if a PM has a different building selected
  // in the top nav.
  building?: Building | null
}

export const ChecklistsDashboardPage = ({
  resident,
  building,
}: ChecklistsDashboardPageProps = {}) => {
  const { t } = useTranslation(['checklists', 'common'])
  // Note: no Outlet context when embedded outside the standalone `/checklists` route (e.g. the
  // Resident Profile) - default to the new table in that case, there's no legacy/new toggle.
  const { uiVersion = 'new' } = useOutletContext<ChecklistsPageOutletContext>() ?? {}
  const residentId = resident?.id
  const globalBuilding = useSelector(selectActiveBuilding)
  const selectedBuilding = building ?? globalBuilding
  const { navigate } = routerFactory.useRouter()
  const { captureEvent, captureDebouncedEvent } = useCaptureEvent()
  const queryClient = useQueryClient()

  const searchParams = useSearchParams({
    initialState: checklistsInitialFilters,
    schema: checklistsQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: checklistsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  useDefaultFiltering({ filters, searchParams })

  const filtersState = filters.filters

  const queryParams: GetChecklistsRequest = {
    buildingId: selectedBuilding?.id,
    clientId: residentId ? [residentId] : filtersState.clientId,
    fromDate: filtersState.fromDate,
    limit: filtersState.limit,
    parentChecklistId: filtersState.parentChecklistId,
    search: filtersState.search?.trim(),
    skip: filtersState.skip,
    sortBy: filtersState.sortBy,
    sortOrder: filtersState.sortOrder,
    status:
      filtersState.status === ChecklistStatusExtendedEnum.enum.all
        ? undefined
        : (filtersState.status as ChecklistStatus),
    toDate: filtersState.toDate,
    type: filtersState.type,
  }

  const {
    data,
    isFetching: isChecklistsFetching,
    error: residentsError,
  } = useGetChecklists(queryParams)
  const { total = 0, data: checklists = [], countByStatus } = data || {}

  const resolveAllMatchingChecklists = async (signal?: AbortSignal): Promise<Checklist[]> => {
    if (!selectedBuilding?.id || !total) return []

    const request: GetChecklistsRequest = { ...queryParams, limit: total, skip: 0 }

    const response = await queryClient.fetchQuery({
      queryFn: ({ signal: querySignal }) => getChecklists(request, querySignal ?? signal),
      queryKey: getChecklistsQueryKey(request),
    })

    return response.data
  }

  // Note: only needed to build the "Resident" filter's options - not rendered at all when
  // `residentId` is set, so skip the fetch entirely.
  const checklistsResidentsOptions = useSelectOptions(
    {
      query: getChecklistsResidents,
      requestData: {
        buildingId: selectedBuilding?.id,
        ...queryParams,
      },
      transform: (residentsData) => {
        const residents = residentsData?.data || []
        return residents.map((client) => ({
          label: `${client.name} - (${client.count})`,
          value: client.clientId,
        }))
      },
    },
    {
      enabled: !!selectedBuilding?.id && !residentId,
      queryKey: ['getChecklistsResidents', selectedBuilding?.id],
    },
  )

  const checklistsTemplatesOptions = useSelectOptions(
    {
      query: getChecklistsTemplates,
      requestData: {
        buildingId: selectedBuilding?.id,
      },
      transform: (templatesData) => {
        const templates = templatesData?.data || []
        return templates.map((template) => ({
          label: template.name,
          value: template.id,
        }))
      },
    },
    {
      enabled: !!selectedBuilding?.id,
      queryKey: ['getChecklistsTemplates', selectedBuilding?.id],
    },
  )

  const { mutateAsync: downloadChecklists } = useExportChecklists()

  const statusTabsOptions: StatusTabOption<ChecklistStatusExtended>[] = useMemo(() => {
    if (!countByStatus) return []

    return [
      {
        // Note: Sum up all the statuses together
        count: Object.values(countByStatus).reduce((acc, curr) => acc + curr, 0),
        label: t('checklists:dashboard.status.all'),
        value: ChecklistStatusExtendedEnum.enum['all'],
      },
      {
        color: getChecklistStatusColor('not-started'),
        count: countByStatus['not-started'] || 0,
        label: getChecklistStatusLabel('not-started', t),
        value: ChecklistStatusExtendedEnum.enum['not-started'],
      },
      {
        color: getChecklistStatusColor('pending-review'),
        count: countByStatus['pending-review'] || 0,
        label: getChecklistStatusLabel('pending-review', t),
        value: ChecklistStatusExtendedEnum.enum['pending-review'],
      },
      {
        color: getChecklistStatusColor('past-due'),
        count: countByStatus['past-due'] || 0,
        label: getChecklistStatusLabel('past-due', t),
        value: ChecklistStatusExtendedEnum.enum['past-due'],
      },
      {
        color: getChecklistStatusColor('completed'),
        count: countByStatus['completed'] || 0,
        label: getChecklistStatusLabel('completed', t),
        value: ChecklistStatusExtendedEnum.enum['completed'],
      },
    ]
  }, [countByStatus, t])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
    captureDebouncedEvent('user_action', {
      name: 'search_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: e.target.value,
    })
  }

  const handleStatusChange = (status: ChecklistStatusExtended) => {
    filters.setFilter('status', status)
    captureEvent('user_action', {
      name: 'status_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: status,
    })
  }

  const handleDateChange = (range: DateRange) => {
    const fromDate = range[0]?.getTime()
    const toDate = range[1]?.getTime()
    filters.setFilters({ fromDate, toDate })
    captureEvent('user_action', {
      fromDate,
      name: 'move_in_date_range_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      toDate,
    })
  }

  const handleAssigneeChange = (assignees: number[]) => {
    filters.setFilter('clientId', assignees)
    captureEvent('user_action', {
      name: 'clientId_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: assignees,
    })
  }

  const handleTypeChange = (type: ChecklistType) => {
    filters.setFilter('type', type)
    captureEvent('user_action', {
      name: 'type_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: type,
    })
  }

  const handleTypeReset = () => {
    filters.setFilter('type', undefined)
    captureEvent('user_action', {
      name: 'type_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: undefined,
    })
  }

  const handleParentChecklistChange = (parentChecklistId: number[]) => {
    filters.setFilter('parentChecklistId', parentChecklistId)
    captureEvent('user_action', {
      name: 'parentChecklistId_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      value: parentChecklistId,
    })
  }

  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const timezone = selectedBuilding?.timezone ?? DEFAULT_TZ

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Checklists',
    fieldType: 'resident',
    rowsQueryKey: [GET_CHECKLISTS_BASE_QUERY_KEY],
  })

  const columns = getChecklistsColumns({ residentColumnSettings, t, timezone })

  const rowActions: RowAction<Checklist>[] = useMemo(() => {
    return [
      {
        label: t('checklists:dashboard.actions.view'),
        onClick: (row) => {
          // Note: Inspection is not supported in the new UI yet, that's why we show old component
          if (row.type === ChecklistTypeEnum.enum.inspection) {
            navigate({
              params: { inspectionId: row.id },
              path: '/dashboard/building-dashboard/checklists/inspection/:inspectionId',
            })
            return
          }

          navigate({
            params: { taskId: row.id },
            path: '/dashboard/building-dashboard/checklists/task/:taskId',
          })
        },
        primary: true,
      },
    ]
  }, [navigate, t])

  const bulkActions = useChecklistsBulkActions({
    building: selectedBuilding,
    statusTab: filtersState.status,
  })

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
    captureEvent('user_action', {
      name: 'sort_change',
      screen: CHECKLISTS_DASHBOARD_ID,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleDownloadCSVClick = () => {
    if (!selectedBuilding) return

    let fileName = 'checklists.csv'
    const trimmed = selectedBuilding.name?.trim()
    if (trimmed) {
      fileName = `${trimmed}-` + fileName
    }

    void downloadChecklists({
      buildingId: selectedBuilding.id,
      fileName,
      ...queryParams,
    })

    captureEvent('user_action', {
      name: 'checklists_csv_download_click',
      screen: CHECKLISTS_DASHBOARD_ID,
    })
  }

  const handleCreateNewTask = () => {
    popupFactory.open(ChecklistsCreateNewTaskModal, {})
  }

  const handleAssignTask = () => {
    popupFactory.open(ChecklistsAssignTaskModal, {
      building: selectedBuilding,
      lockResident: resident,
      showCreateNewTask: handleCreateNewTask,
    })
  }

  return uiVersion === 'new' ? (
    <TableGroup
      id={CHECKLISTS_DASHBOARD_ID}
      columns={columns}
      data={checklists}
      error={residentsError}
      loading={isChecklistsFetching}
      getRowKey={(checklist) => checklist.id}
      rowActions={rowActions}
      mode="bulk"
      bulkActions={bulkActions}
      selectAllMatching={{ resolveAll: resolveAllMatchingChecklists, total }}
      // Note: Resident Profile embed - status tabs row is already in use, so title + "Assign
      // Task" get their own row above it, inside the same card (see EL-14186 Figma).
      title={
        residentId && (
          <Typography as="h2" size="heading-small" color="black800">
            {t('checklists:page.title')}
          </Typography>
        )
      }
      titleActions={
        residentId && (
          <Button size="small" onClick={handleAssignTask}>
            {t('checklists:page.assignTask')}
            <Icon icon={PlusIcon} />
          </Button>
        )
      }
      noShadow={!!residentId}
      filters={
        <>
          {/* Note: Resident Profile embed moves the date range into the additional filters row -
          space constraints on the narrower embedded layout (see EL-14186 Figma). */}
          {residentId && (
            <DatePickerInput
              mode="range"
              placeholder={t('checklists:dashboard.filters.dateRangePlaceholder')}
              size="small"
              value={dateRange}
              maxDate={toZonedTime(new Date(), timezone)}
              onRangeChange={handleDateChange}
            />
          )}
          <Select
            {...checklistsTemplatesOptions.selectProps}
            searchable
            size="small"
            value={filters.getFilter('parentChecklistId') ?? []}
            type="multi"
            placeholder={t('checklists:dashboard.filters.namePlaceholder')}
            dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
            onChange={handleParentChecklistChange}
            onReset={() => handleParentChecklistChange([])}
          />
          <Select
            options={makeChecklistTypeOptions(t)}
            size="small"
            value={filters.getFilter('type') ?? null}
            placeholder={t('checklists:dashboard.filters.typePlaceholder')}
            onChange={handleTypeChange}
            onReset={handleTypeReset}
          />
          {!residentId && (
            <Select
              {...checklistsResidentsOptions.selectProps}
              searchable
              size="small"
              type="multi"
              value={filters.getFilter('clientId') ?? []}
              placeholder={t('checklists:dashboard.filters.residentPlaceholder')}
              dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
              onChange={handleAssigneeChange}
              onReset={() => handleAssigneeChange([])}
            />
          )}
        </>
      }
      navigation={{
        actions: (
          <Button
            onlyBox
            size="small"
            use="secondary"
            title={t('checklists:dashboard.actions.exportCsv')}
            onClick={handleDownloadCSVClick}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        datePicker: residentId ? undefined : (
          <DatePickerInput
            mode="range"
            placeholder={t('checklists:dashboard.filters.dateRangePlaceholder')}
            size="small"
            value={dateRange}
            maxDate={toZonedTime(new Date(), timezone)}
            onRangeChange={handleDateChange}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('checklists:dashboard.filters.searchPlaceholder')}
            size="small"
            value={filters.getFilter('search')}
            onChange={handleSearchChange}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusTabsOptions}
            value={filters.getFilter('status') ?? null}
            disabled={checklistsResidentsOptions.isFetching || isChecklistsFetching}
            onChange={handleStatusChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      tableFilters={filters}
      sortBy={filters.getFilter('sortBy')}
      sortOrder={filters.getFilter('sortOrder')}
      onSort={handleSort}
    />
  ) : (
    <LegacyChecklistsDashboard />
  )
}
